import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  ViewEncapsulation,
  computed,
  inject,
  resource,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { IDownlinkJob, IListado } from 'modelos/src';
import { DownlinkJobsService } from '../../../../../auxiliares/servicios/http/downlink-jobs.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { BreadcrumbComponent } from '../../../../../componentes/breadcrumb/breadcrumb.component';
import {
  CADENCIA,
  ICONOS_ORIGEN,
  MOTIVOS_REZAGO,
  MotivoRezago,
  Rezagado,
  SELECT_JOBS_CAMPANA,
  TONO,
  TONOS_ESTADO,
  armar,
  desglose,
  duracion,
  estadoEncendido,
  faltaEstimado,
  fechaHora,
  haceCuanto,
  ordenMotivo,
  presentacionJob,
  textoObjetivo,
  tituloEncendido,
  tonoRezago,
} from '../seguimiento.helpers';

/** Tope de envíos de un encendido o apagado. Uno a 1.500 luminarias tiene 1.500 envíos o más. */
const TOPE = 8000;

/** Cuántos rezagados se listan sin filtro. Con filtro se listan todos los del motivo. */
const TOPE_TABLA = 100;

/**
 * El detalle de un encendido o apagado.
 *
 * Lo que se viene a buscar acá no es el progreso —eso ya estaba en el listado—
 * sino **quiénes quedaron atrás y por qué**: al que no comunicaba hay que ir a
 * verlo (y ya tiene su evento de comunicación); al que agotó los envíos,
 * revisarle la señal; al que confirmó y no aplicó, mirarle la configuración.
 *
 * Los que no comunicaban arrancan fuera de la tabla: en un encendido a un grupo
 * grande suelen ser la mayoría y tapan a los que sí piden algo. Se cuentan igual
 * y se muestran con un clic.
 */
@Component({
  selector: 'app-detalle-encendido',
  standalone: true,
  imports: [
    DecimalPipe,
    BOTONES,
    TooltipDirective,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
    BreadcrumbComponent,
  ],
  templateUrl: './detalle-encendido.component.html',
  styleUrl: '../seguimiento.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DetalleEncendidoComponent {
  private readonly http = inject(DownlinkJobsService);
  private readonly ruta = inject(ActivatedRoute);

  /** Sin permiso para ver dispositivos LoRaWAN, el nodo va sin enlace. */
  protected readonly verNodos = inject(HelperService).tienePermiso(
    'Administración - Ver dispositivos Lorawan',
  );

  protected readonly MOTIVOS = MOTIVOS_REZAGO;

  private readonly params = toSignal(this.ruta.paramMap, {
    initialValue: undefined,
  });
  private readonly id = computed(() => this.params()?.get('id') ?? '');

  protected readonly motivoFiltro = signal<MotivoRezago | ''>('');
  protected readonly verSinComunicacion = signal(false);

  constructor() {
    // Polleo con reload(): un cambio en los params vaciaría el detalle.
    const intervalo = setInterval(() => {
      if (document.visibilityState === 'visible') this.jobs.reload();
    }, CADENCIA.listado * 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(intervalo));
  }

  protected readonly jobs = resource({
    params: () => ({ id: this.id() }),
    loader: ({ params }): Promise<IListado<IDownlinkJob> | undefined> => {
      if (!params.id) return Promise.resolve(undefined);
      return this.http.getFiltered({
        filter: JSON.stringify({ idEjecucion: params.id }),
        sort: 'fechaCreacion',
        limit: TOPE,
        includeChildren: true,
        excludeTotalCount: true,
        select: `${SELECT_JOBS_CAMPANA} descripcion`,
      });
    },
  });

  protected readonly campana = computed(() => {
    const lote = this.jobs.value()?.datos ?? [];
    return lote.length ? armar(this.id(), lote) : undefined;
  });

  /** "Encender al 80%" o "Apagar". */
  protected readonly titulo = computed(() => {
    const c = this.campana();
    return c ? tituloEncendido(c, this.jobs.value()?.datos ?? []) : undefined;
  });

  protected readonly d = computed(() => {
    const c = this.campana();
    return c ? desglose(c) : undefined;
  });

  protected readonly porcentaje = computed(() =>
    Math.round((this.campana()?.progreso ?? 0) * 100),
  );

  protected readonly estado = computed(() => {
    const c = this.campana();
    const d = this.d();
    return c && d ? estadoEncendido(c, d) : undefined;
  });

  protected readonly objetivo = computed(() => {
    const c = this.campana();
    return c ? textoObjetivo(c) : undefined;
  });

  protected readonly iconoOrigen = computed(
    () => ICONOS_ORIGEN[this.campana()?.origen ?? ''] ?? 'campaign',
  );

  protected readonly falta = computed(() => {
    const c = this.campana();
    const ms = c ? faltaEstimado(c) : undefined;
    return ms === undefined ? undefined : duracion(ms);
  });

  /** Cuánto duró (terminado) o cuánto lleva (en curso). */
  protected readonly lleva = computed(() => {
    const c = this.campana();
    if (!c) return undefined;
    const desde = new Date(c.inicio).getTime();
    const hasta = c.terminada
      ? new Date(c.ultimaNovedad).getTime()
      : Date.now();
    if (Number.isNaN(desde) || Number.isNaN(hasta) || hasta < desde) {
      return undefined;
    }
    return duracion(hasta - desde);
  });

  protected readonly segmentos = computed(() => {
    const c = this.campana();
    const d = this.d();
    if (!c || !d || !c.nodos) return [];
    return [
      {
        clave: 'aplicaron',
        etiqueta: 'Aplicaron',
        valor: d.aplicaron,
        tono: TONO.ok,
      },
      {
        clave: 'enCurso',
        etiqueta: 'En curso',
        valor: d.enCurso,
        tono: TONO.info,
      },
      {
        clave: 'aRevisar',
        etiqueta: 'A revisar',
        valor: d.aRevisar,
        tono: TONO.aviso,
      },
      {
        clave: 'sinComunicacion',
        etiqueta: 'Sin comunicación',
        valor: d.sinComunicacion,
        tono: TONO.neutro,
      },
      {
        clave: 'reemplazados',
        etiqueta: 'Reemplazados',
        valor: d.reemplazados,
        tono: TONO.tenue,
      },
    ]
      .filter((s) => s.valor)
      .map((s) => ({ ...s, ancho: (s.valor / c.nodos) * 100 }));
  });

  /** Los envíos por estado: son pasos, no nodos. */
  protected readonly estados = computed(() => {
    const porEstado = this.campana()?.porEstado ?? {};
    const total = Object.values(porEstado).reduce((s, n) => s + n, 0) || 1;
    return Object.entries(porEstado)
      .map(([estado, cuantos]) => {
        const p = presentacionJob(estado);
        return {
          estado,
          cuantos,
          ...p,
          color: TONOS_ESTADO[p.tono],
          ancho: (cuantos / total) * 100,
        };
      })
      .sort((a, b) => b.cuantos - a.cuantos);
  });

  protected readonly porMotivo = computed(() => {
    const cuenta = new Map<MotivoRezago, number>();
    for (const r of this.campana()?.rezagados ?? []) {
      cuenta.set(r.motivo, (cuenta.get(r.motivo) ?? 0) + 1);
    }
    // Por urgencia, igual que la tabla: por cantidad pondría "todavía en
    // curso" primero, que es lo único que no hay que mirar.
    return [...cuenta.entries()]
      .map(([motivo, cuantos]) => ({
        motivo,
        cuantos,
        tono: tonoRezago(motivo),
        ...MOTIVOS_REZAGO[motivo],
      }))
      .sort((a, b) => ordenMotivo(a.motivo) - ordenMotivo(b.motivo));
  });

  private readonly filtrados = computed<Rezagado[]>(() => {
    const todos = this.campana()?.rezagados ?? [];
    const motivo = this.motivoFiltro();
    if (motivo) return todos.filter((r) => r.motivo === motivo);
    return this.verSinComunicacion()
      ? todos
      : todos.filter((r) => r.motivo !== 'offline');
  });

  protected readonly rezagadosVisibles = computed(() => {
    const lista = this.filtrados();
    return this.motivoFiltro() ? lista : lista.slice(0, TOPE_TABLA);
  });

  protected readonly cuantosFiltrados = computed(() => this.filtrados().length);

  protected readonly sinComunicacionOcultos = computed(() =>
    this.motivoFiltro() || this.verSinComunicacion()
      ? 0
      : (this.d()?.sinComunicacion ?? 0),
  );

  protected alternarMotivo(motivo: MotivoRezago): void {
    this.motivoFiltro.set(this.motivoFiltro() === motivo ? '' : motivo);
  }

  protected tono(motivo: MotivoRezago): string {
    return tonoRezago(motivo);
  }

  protected cuando(iso: string): string {
    return haceCuanto(iso);
  }

  protected fecha(iso: string): string {
    return fechaHora(iso);
  }

  protected recargar(): void {
    this.jobs.reload();
  }
}
